import React, { useMemo } from 'react';
import { Shirt, Users, Download } from 'lucide-react';
import { Button } from '../ui/button';

const SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL'];

export const TshirtRecapSection = ({ participants, events, participantFilter }) => {
  const recap = useMemo(() => {
    const byEvent = {};
    const total = {};
    SIZES.forEach(s => { total[s] = 0; });
    total['Non renseigne'] = 0;

    const filtered = participantFilter && participantFilter !== 'all'
      ? participants.filter(p => p.event_id === participantFilter)
      : participants;

    filtered.forEach(reg => {
      const eventTitle = reg.event_title || reg.event_id || 'Inconnu';
      if (!byEvent[eventTitle]) {
        byEvent[eventTitle] = {};
        SIZES.forEach(s => { byEvent[eventTitle][s] = 0; });
        byEvent[eventTitle]['Non renseigne'] = 0;
        byEvent[eventTitle]._total = 0;
      }

      // Captain / solo participant tshirt
      const sz = reg.tshirt_size;
      if (sz && SIZES.includes(sz)) {
        byEvent[eventTitle][sz]++;
        total[sz]++;
      } else {
        byEvent[eventTitle]['Non renseigne']++;
        total['Non renseigne']++;
      }
      byEvent[eventTitle]._total++;

      // Team members
      if (reg.team_members && Array.isArray(reg.team_members)) {
        reg.team_members.forEach(m => {
          const msz = m.tshirt_size;
          if (msz && SIZES.includes(msz)) {
            byEvent[eventTitle][msz]++;
            total[msz]++;
          } else {
            byEvent[eventTitle]['Non renseigne']++;
            total['Non renseigne']++;
          }
          byEvent[eventTitle]._total++;
        });
      }
    });

    const grandTotal = Object.values(total).reduce((a, b) => a + b, 0);
    return { byEvent, total, grandTotal };
  }, [participants, participantFilter]);

  const exportCSV = () => {
    let csv = 'Evenement,' + SIZES.join(',') + ',Non renseigne,Total\n';
    Object.entries(recap.byEvent).forEach(([event, counts]) => {
      csv += `"${event}",${SIZES.map(s => counts[s]).join(',')},${counts['Non renseigne']},${counts._total}\n`;
    });
    csv += `TOTAL,${SIZES.map(s => recap.total[s]).join(',')},${recap.total['Non renseigne']},${recap.grandTotal}\n`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'recap_tailles_tshirt.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  if (recap.grandTotal === 0) {
    return (
      <div className="bg-slate-50 border border-slate-200 p-6 text-center text-slate-400" data-testid="tshirt-recap-empty">
        <Shirt className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">Aucune inscription avec taille T-shirt</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="tshirt-recap-section">
      {/* Total summary bar */}
      <div className="bg-white border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Shirt className="w-5 h-5 text-brand" />
            <h3 className="font-heading font-bold text-sm uppercase">Total Tailles</h3>
            <span className="text-xs text-slate-400">({recap.grandTotal} personnes)</span>
          </div>
          <Button variant="outline" size="sm" onClick={exportCSV} className="gap-1 text-xs h-7" data-testid="tshirt-export-csv">
            <Download className="w-3 h-3" /> CSV
          </Button>
        </div>
        <div className="grid grid-cols-7 gap-2">
          {SIZES.map(sz => (
            <div key={sz} className="text-center p-2 bg-slate-50 border border-slate-100" data-testid={`tshirt-total-${sz}`}>
              <p className="font-heading font-bold text-lg text-brand">{recap.total[sz]}</p>
              <p className="text-[10px] font-bold uppercase text-slate-500">{sz}</p>
            </div>
          ))}
          <div className="text-center p-2 bg-amber-50 border border-amber-100" data-testid="tshirt-total-unknown">
            <p className="font-heading font-bold text-lg text-amber-600">{recap.total['Non renseigne']}</p>
            <p className="text-[10px] font-bold uppercase text-amber-500">N/A</p>
          </div>
        </div>
      </div>

      {/* Per event breakdown */}
      {Object.entries(recap.byEvent).length > 1 && (
        <div className="space-y-2">
          {Object.entries(recap.byEvent).map(([event, counts]) => (
            <div key={event} className="bg-white border border-slate-200 p-3">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-3.5 h-3.5 text-slate-400" />
                <p className="text-xs font-heading font-bold uppercase text-slate-600 truncate">{event}</p>
                <span className="text-[10px] text-slate-400 ml-auto">{counts._total} pers.</span>
              </div>
              <div className="grid grid-cols-7 gap-1">
                {SIZES.map(sz => (
                  <div key={sz} className="text-center py-1">
                    <span className={`text-sm font-bold ${counts[sz] > 0 ? 'text-slate-800' : 'text-slate-300'}`}>{counts[sz]}</span>
                    <p className="text-[9px] text-slate-400">{sz}</p>
                  </div>
                ))}
                <div className="text-center py-1">
                  <span className={`text-sm font-bold ${counts['Non renseigne'] > 0 ? 'text-amber-500' : 'text-slate-300'}`}>{counts['Non renseigne']}</span>
                  <p className="text-[9px] text-slate-400">N/A</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
